import {useCallback, useContext, useMemo, useRef, useState} from 'react';
import {
  CreateWorkspaceRequestPayload,
  PersonContext,
  RequestsApiService,
  useService,
  Workspace,
  WorkspacesApiService,
} from '@innowise-group/core';
import {Elements} from 'react-flow-renderer';
import {useTranslation} from 'react-i18next';
import {endOfDay, format, parse, setHours} from 'date-fns';
import {BehaviorSubject, finalize} from 'rxjs';
import {BookingWorkspaceModeFormValues, DateRange} from '../../components/booking-workspace-mode-form';
import {WorkspaceElementData} from '../../components/workspace-view';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {mapWorkspaceToElements} from '../../utilities/element-mapper.utility';
import {mapWorkspacesForBookingMode} from '../../utilities/map-booking-mode-workspaces.utility';
import {FloorData} from './use-plan-details.hook';
import {BookingWorkspaceValues, RequestType} from '@shared-components';

export interface UseWorkspaceBookingResult {
  bookingWorkspaceMode: (data: BookingWorkspaceModeFormValues) => void;
  bookWorkspace: () => void;
  bookChosenWorkspace: (workspaceId: string, roomId: string) => void;
  bookWorkspaceDateRange: {range: DateRange} | null;
  /** Live list or booking-mode projection, depending on current filter. */
  syncWorkspaces: () => void;
  /** True when booking toggle has a complete date range applied. */
  isBookingModeActive: () => boolean;
}

export const useWorkspaceBooking = (
  {floor, address}: FloorData,
  setIsPlanLoading: (value: boolean) => void,
  floorId: string,
  workspaces$: BehaviorSubject<Elements<WorkspaceElementData>>,
  syncLiveWorkspaces: () => void,
  onSyncError?: (error: unknown) => void,
): UseWorkspaceBookingResult => {
  const person = useContext(PersonContext);
  const {t} = useTranslation();
  const [bookWorkspaceDateRange, setBookWorkspaceDateRange] = useState<{range: DateRange} | null>(null);
  const bookWorkspaceDateRangeRef = useRef(bookWorkspaceDateRange);
  bookWorkspaceDateRangeRef.current = bookWorkspaceDateRange;
  const workspacesApi = useService(WorkspacesApiService);
  const requestsApi = useService(RequestsApiService);
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const workspacesValues = workspaces$.getValue();

  const workspaceList = useMemo(() => {
    return workspacesValues.map(({data: {id, number, bookedStatus, activeRequests, status, room}}) => {
      const excludeDates = bookedStatus.map((status) => ({
        employeeId: status.employee.id,
        range: {
          start: status.range.startDay ? parse(status.range.startDay, 'dd.MM.yyyy', new Date()) : null,
          end: status.range.endDay ? parse(status.range.endDay, 'dd.MM.yyyy', new Date()) : null,
        },
      }));
      const activeDates = activeRequests.map((status) => ({
        employeeId: status.employee.id,
        range: {
          start: status.range.startDay ? parse(status.range.startDay, 'dd.MM.yyyy', new Date()) : null,
          end: status.range.endDay ? parse(status.range.endDay, 'dd.MM.yyyy', new Date()) : null,
        },
      }));
      return {id, excludeDates, activeDates, status, room, number};
    });
  }, [workspacesValues]);

  const replaceWorkspacesPreservingDrafts = useCallback(
    (next: Elements<WorkspaceElementData>) => {
      const drafts = workspaces$.getValue().filter((element) => element.data?.isDraft);
      workspaces$.next(drafts.length ? [...next, ...drafts] : next);
    },
    [workspaces$],
  );

  const getBookingModeFilterRange = useCallback(() => {
    const range = bookWorkspaceDateRangeRef.current?.range;
    if (!range?.startDay || !range?.endDay) {
      return null;
    }

    return {
      start: new Date(range.startDay),
      end: endOfDay(new Date(range.endDay)),
    };
  }, []);

  const applyBookingProjection = useCallback(
    (workspaces: Workspace[]) => {
      const filterRange = getBookingModeFilterRange();
      const projectedById = new Map(
        mapWorkspacesForBookingMode(workspaces, filterRange).map((workspace) => [workspace.id, workspace]),
      );

      const merged = workspaces$
        .getValue()
        .filter((element) => !element.data?.isDraft)
        .map((element) => {
          const projected = projectedById.get(element.data.id);
          if (projected) {
            projectedById.delete(element.data.id);
            return mapWorkspaceToElements([projected], true)[0];
          }

          const repainted = mapWorkspacesForBookingMode([element.data], filterRange)[0];
          return mapWorkspaceToElements([repainted], true)[0];
        });

      const extra = mapWorkspaceToElements(
        mapWorkspacesForBookingMode(Array.from(projectedById.values()), filterRange),
        true,
      );
      replaceWorkspacesPreservingDrafts([...merged, ...extra]);
    },
    [getBookingModeFilterRange, replaceWorkspacesPreservingDrafts, workspaces$],
  );

  const isBookingModeActive = useCallback(() => {
    const range = bookWorkspaceDateRangeRef.current?.range;
    return Boolean(range?.startDay && range?.endDay);
  }, []);

  const syncBookingProjection = useCallback(
    (options?: {showLoader?: boolean}) => {
      const range = bookWorkspaceDateRangeRef.current?.range;
      if (!range?.startDay || !range?.endDay) {
        syncLiveWorkspaces();
        return;
      }

      const startDayFormatted = format(new Date(range.startDay), 'yyyy-MM-dd');
      const endDayFormatted = format(new Date(range.endDay), 'yyyy-MM-dd');
      const showLoader = Boolean(options?.showLoader);

      if (showLoader) {
        setIsPlanLoading(true);
      }

      workspacesApi
        .bookingWorkspaceMode(floorId, startDayFormatted, endDayFormatted)
        .pipe(
          finalize(() => {
            if (showLoader) {
              setIsPlanLoading(false);
            }
          }),
        )
        .subscribe({
          next: applyBookingProjection,
          error: (error) => onSyncError?.(error),
        });
    },
    [applyBookingProjection, floorId, onSyncError, setIsPlanLoading, syncLiveWorkspaces, workspacesApi],
  );

  const syncWorkspaces = useCallback(() => {
    if (isBookingModeActive()) {
      syncBookingProjection({showLoader: false});
      return;
    }
    syncLiveWorkspaces();
  }, [isBookingModeActive, syncBookingProjection, syncLiveWorkspaces]);

  const createWorkspaceRequest = useCallback(
    ({seat, startDay, endDay, comment, requestType}: BookingWorkspaceValues) => {
      const payload: CreateWorkspaceRequestPayload = {
        workspace: seat && Number(seat),
        request_type: requestType,
        employee: person?.id && Number(person?.id),
        comment,
      };

      if (requestType === RequestType.TemporalOccupation) {
        payload.occupation_start_date = startDay && setHours(new Date(startDay), 9).toISOString();
        payload.occupation_end_date = endDay && setHours(new Date(endDay), 18).toISOString();
      }

      return requestsApi.createWorkspaceRequest(payload).pipe(
        finalize(() => {
          syncWorkspaces();
        }),
      );
    },
    [person?.id, requestsApi, syncWorkspaces],
  );

  const bookWorkspace = useCallback(() => {
    floorsModalsFacade.openBookingWorkspaceForm(
      createWorkspaceRequest,
      {address, floor, ...bookWorkspaceDateRange?.range},
      t('modals.requestWorkspaceTitle'),
      t('buttons.book'),
      person,
      workspaceList,
    );
  }, [
    bookWorkspaceDateRange?.range,
    createWorkspaceRequest,
    address,
    floor,
    floorsModalsFacade,
    person,
    t,
    workspaceList,
  ]);

  const bookChosenWorkspace = useCallback(
    (workspaceId: string, roomId: string) => {
      floorsModalsFacade.openBookingWorkspaceForm(
        createWorkspaceRequest,
        {address, floor, room: roomId, seat: workspaceId, ...bookWorkspaceDateRange?.range},
        t('modals.requestWorkspaceTitle'),
        t('buttons.book'),
        person,
        workspaceList,
      );
    },
    [
      floorsModalsFacade,
      createWorkspaceRequest,
      address,
      floor,
      bookWorkspaceDateRange?.range,
      t,
      person,
      workspaceList,
    ],
  );

  const bookingWorkspaceMode = useCallback(
    (data: BookingWorkspaceModeFormValues) => {
      const {startDay, endDay, bookingMode} = data;
      if (bookingMode && startDay && endDay) {
        const nextRange = {range: {startDay, endDay}};
        bookWorkspaceDateRangeRef.current = nextRange;
        setBookWorkspaceDateRange(nextRange);
        syncBookingProjection({showLoader: true});
        return;
      }

      if (bookingMode && (startDay === null || endDay === null)) {
        // Leaving incomplete booking mode only refreshes if mode was active.
        const wasActive = Boolean(bookWorkspaceDateRangeRef.current);
        bookWorkspaceDateRangeRef.current = null;
        setBookWorkspaceDateRange(null);
        setIsPlanLoading(false);
        if (wasActive) {
          syncLiveWorkspaces();
        }
        return;
      }

      if (!bookingMode) {
        // Avoid redundant getWorkspaces when booking mode was never applied (e.g. enter edit).
        const wasActive = Boolean(bookWorkspaceDateRangeRef.current);
        bookWorkspaceDateRangeRef.current = null;
        setBookWorkspaceDateRange(null);
        setIsPlanLoading(false);
        if (wasActive) {
          syncLiveWorkspaces();
        }
      }
    },
    [setIsPlanLoading, syncBookingProjection, syncLiveWorkspaces],
  );

  return {
    bookingWorkspaceMode,
    bookWorkspace,
    bookChosenWorkspace,
    bookWorkspaceDateRange,
    syncWorkspaces,
    isBookingModeActive,
  };
};
